import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { EventDto } from '@odo/shared-ui';
import { useActiveEvents } from '../../hooks/useEvents';
import EventBanner from './EventBanner';
import EventRotationControls from './EventRotationControls';
import { prefersReducedMotion, useEventRotation } from './useEventRotation';

const NO_EVENTS: never[] = [];

/** Must match the `event-slide-in`/`event-slide-out` durations in
 *  tailwind.config.js - it is what unmounts the outgoing banner, and unmounting
 *  early would cut the slide off mid-flight. */
const SLIDE_DURATION_MS = 500;

// Renders nothing while loading and nothing on error - mounted on every
// authenticated page (SidebarLayout), so a backend hiccup here must never
// break the sidebar.
//
// EVERY live event, one slot at a time. The database used to guarantee at most
// one (events_one_banner_at_a_time); 11_drop_one_banner_constraint.sql removed
// it once this component learned to rotate, so `/events/active` may now return
// several - already narrowed server-side to the ones this practice may see.
//
// One slot rather than a stack: this sits in the sticky top bar of every page,
// so a second row would eat viewport height from every screen. The endpoint
// orders by `startsAt` ascending, so rotation runs soonest-first.
const EventBannerStrip: React.FC = () => {
    const { data } = useActiveEvents();
    const events = data ?? NO_EVENTS;

    const { activeIndex, select, setHeld } = useEventRotation(events.length);

    // The banner being slid off-screen. Held in state (not derived) because it
    // has to outlive the render that replaced it - long enough to animate out.
    const [outgoing, setOutgoing] = useState<EventDto | null>(null);
    const shownRef = useRef<EventDto | null>(null);

    // `activeIndex` is clamped against `events.length` inside the hook, but the
    // list can also be empty - in which case there is nothing to render at all.
    const active = events[activeIndex];

    useEffect(() => {
        const previous = shownRef.current;
        shownRef.current = active ?? null;

        // Nothing to slide from on first paint, and `useActiveEvents` refetches
        // on an interval, so the same event arrives as a new object routinely -
        // compare ids, not identity, or every refetch would animate.
        if (!previous || !active || previous.id === active.id) return undefined;
        // Auto-advance is already off under this setting; this covers the slide
        // a manual dot click would otherwise still trigger.
        if (prefersReducedMotion()) return undefined;

        setOutgoing(previous);
        const timer = setTimeout(() => setOutgoing(null), SLIDE_DURATION_MS);
        return () => clearTimeout(timer);
    }, [active]);

    const hold = useCallback(() => setHeld(true), [setHeld]);
    const release = useCallback(() => setHeld(false), [setHeld]);

    if (!active) return null;

    const isRotating = events.length > 1;

    return (
        // Hover and focus hold rotation so it cannot advance out from under a
        // pointer on its way to the banner, or under a keyboard reader inside it.
        //
        // `overflow-hidden` is what makes the slide a slide: it clips both
        // banners to the strip so they travel behind its edges instead of over
        // the page. Safe to clip here - the dots sit inside the banner and the
        // detail modal is portaled out.
        <div
            className="relative overflow-hidden"
            onMouseEnter={isRotating ? hold : undefined}
            onMouseLeave={isRotating ? release : undefined}
            onFocusCapture={isRotating ? hold : undefined}
            onBlurCapture={isRotating ? release : undefined}
            role={isRotating ? 'region' : undefined}
            aria-roledescription={isRotating ? 'carousel' : undefined}
            aria-label={isRotating ? 'Event announcements' : undefined}
        >
            {/* Absolute, so the incoming banner below keeps defining the strip's
                height and it never collapses mid-transition. Inert: a screen
                reader must not meet the same strip twice, and a pointer must not
                catch an RSVP on its way out. */}
            {outgoing && (
                <div
                    // Keyed, or a second transition starting inside the first
                    // one's 500ms keeps the same element: CSS restarts an
                    // animation on remount, not on a content swap, so the new
                    // outgoing banner would pick up the old one's timeline
                    // mid-flight and slide out from wherever that had reached.
                    key={outgoing.id}
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none animate-event-slide-out"
                >
                    <EventBanner event={outgoing} />
                </div>
            )}

            <div key={active.id} className={outgoing ? 'animate-event-slide-in' : undefined}>
                <EventBanner
                    // Keyed (above) so switching events remounts rather than
                    // re-rendering in place: EventBannerRsvp's countdown and join
                    // state belong to one event, and carrying them across would
                    // show the wrong event's.
                    event={active}
                    onModalOpenChange={isRotating ? setHeld : undefined}
                    controls={
                        isRotating ? (
                            <EventRotationControls
                                events={events}
                                activeIndex={activeIndex}
                                onSelect={select}
                            />
                        ) : undefined
                    }
                />
            </div>
        </div>
    );
};

export default EventBannerStrip;
